import { MutationResult } from "@/api/utils/types"
import { useMutation } from "@/api/utils/useQuery"
import { BackofficeRestMutations } from "@/api/backofficeRest/mutations"
import type {
  CreateRewardPayload,
  DeleteRewardPayload,
  DrawIdPayload,
  UpdateDrawPayload,
  UpdateRewardPayload,
  UpdateWinnerPayload,
} from "@/api/backofficeRest/mutations"
import {
  CreateDrawRequest,
  CreateDrawResponse,
  DrawRewardRequest,
  DrawRewardResponse,
  PrizeDrawEntryWinner,
  RewardConfiguration,
  RewardConfigurationType,
  UpdatePrizeDrawEntryWinnerRequest,
} from "@/api/backofficeRest/generated/model"
import {
  ICreateDrawInput,
  IUpdateDrawInput,
  ICreateDrawReward,
  IDrawRewardInput,
  IUpdateDrawReward,
  IDeleteDrawReward,
  IUpdateWinner,
} from "./LoyaltyMutator.types"

type DrawIdInput = { drawId: string }

// YEAR_OF_ENERGY carries its value as single- and dual-fuel reward configuration
// rows rather than a single creditValuePence; other types send none.
const toRewardConfiguration = (
  reward: IDrawRewardInput,
): RewardConfiguration[] | undefined => {
  const config: RewardConfiguration[] = []
  if (reward.singleFuelPence !== null) {
    config.push({
      type: RewardConfigurationType.SINGLE_FUEL_PENCE,
      value: String(reward.singleFuelPence),
    })
  }
  if (reward.dualFuelPence !== null) {
    config.push({
      type: RewardConfigurationType.DUAL_FUEL_PENCE,
      value: String(reward.dualFuelPence),
    })
  }
  return config.length > 0 ? config : undefined
}

const toRewardRequest = (reward: IDrawRewardInput): DrawRewardRequest => ({
  name: reward.name,
  type: reward.type,
  quantity: reward.quantity,
  creditValuePence: reward.creditValuePence ?? undefined,
  rewardConfiguration: toRewardConfiguration(reward),
})

export interface ILoyaltyMutator {
  createDraw(): MutationResult<ICreateDrawInput, CreateDrawResponse>
  updateDraw(): MutationResult<IUpdateDrawInput>
  createReward(): MutationResult<ICreateDrawReward, DrawRewardResponse>
  updateReward(): MutationResult<IUpdateDrawReward, DrawRewardResponse>
  deleteReward(): MutationResult<IDeleteDrawReward>
  updateWinner(): MutationResult<IUpdateWinner, PrizeDrawEntryWinner>
  closeDraw(): MutationResult<DrawIdInput>
  reprocessWinners(): MutationResult<DrawIdInput>
  notifyWinners(): MutationResult<DrawIdInput>
  grantPrizes(): MutationResult<DrawIdInput>
}

export class LoyaltyMutatorClass implements ILoyaltyMutator {
  createDraw(): MutationResult<ICreateDrawInput, CreateDrawResponse> {
    return useMutation({
      ...BackofficeRestMutations.loyalty.createDraw(),
      serializerFn: (data: ICreateDrawInput): CreateDrawRequest => ({
        name: data.name,
        displayName: data.displayName,
        startDateTime: data.startDateTime,
        entryDeadlineDateTime: data.entryDeadlineDateTime,
        completionDateTime: data.completionDateTime,
        winnerPublicationDateTime: data.winnerPublicationDateTime,
        winnerNotificationDateTime: data.winnerNotificationDateTime,
        rewards: [],
      }),
    })
  }

  updateDraw(): MutationResult<IUpdateDrawInput> {
    return useMutation({
      ...BackofficeRestMutations.loyalty.updateDraw(),
      serializerFn: (data: IUpdateDrawInput): UpdateDrawPayload => ({
        drawId: Number(data.drawId),
        body: {
          name: data.name,
          displayName: data.displayName,
          startDateTime: data.startDateTime,
          entryDeadlineDateTime: data.entryDeadlineDateTime,
          completionDateTime: data.completionDateTime,
          winnerPublicationDateTime: data.winnerPublicationDateTime,
          winnerNotificationDateTime: data.winnerNotificationDateTime,
        },
      }),
    })
  }

  createReward(): MutationResult<ICreateDrawReward, DrawRewardResponse> {
    return useMutation({
      ...BackofficeRestMutations.loyalty.createReward(),
      serializerFn: (data: ICreateDrawReward): CreateRewardPayload => ({
        drawId: Number(data.drawId),
        body: toRewardRequest(data.reward),
      }),
    })
  }

  updateReward(): MutationResult<IUpdateDrawReward, DrawRewardResponse> {
    return useMutation({
      ...BackofficeRestMutations.loyalty.updateReward(),
      serializerFn: (data: IUpdateDrawReward): UpdateRewardPayload => ({
        drawId: Number(data.drawId),
        rewardId: Number(data.rewardId),
        body: toRewardRequest(data.reward),
      }),
    })
  }

  deleteReward(): MutationResult<IDeleteDrawReward> {
    return useMutation({
      ...BackofficeRestMutations.loyalty.deleteReward(),
      serializerFn: (data: IDeleteDrawReward): DeleteRewardPayload => ({
        drawId: Number(data.drawId),
        rewardId: Number(data.rewardId),
      }),
    })
  }

  updateWinner(): MutationResult<IUpdateWinner, PrizeDrawEntryWinner> {
    return useMutation({
      ...BackofficeRestMutations.loyalty.updateWinner(),
      serializerFn: (data: IUpdateWinner): UpdateWinnerPayload => {
        const body: UpdatePrizeDrawEntryWinnerRequest = {}
        if (data.county !== undefined) body.county = data.county
        if (data.nameVisibilityOptOut !== undefined) {
          body.nameVisibilityOptOut = data.nameVisibilityOptOut
        }
        if (data.status !== undefined) body.status = data.status
        return {
          drawId: Number(data.drawId),
          winnerId: Number(data.winnerId),
          body,
        }
      },
    })
  }

  // Closes the draw AND selects winners in one async BE step (WS.23). The button
  // is the "Select winners" action; status drives the lifecycle thereafter.
  closeDraw(): MutationResult<DrawIdInput> {
    return useMutation({
      ...BackofficeRestMutations.loyalty.closeDraw(),
      serializerFn: (data: DrawIdInput): DrawIdPayload => ({
        drawId: Number(data.drawId),
      }),
    })
  }

  // Re-selects winners for rejected slots — synchronous (be PR #9903 / SO-31265),
  // so the caller refetches the winners list on success rather than polling.
  reprocessWinners(): MutationResult<DrawIdInput> {
    return useMutation({
      ...BackofficeRestMutations.loyalty.reprocessWinners(),
      serializerFn: (data: DrawIdInput): DrawIdPayload => ({
        drawId: Number(data.drawId),
      }),
    })
  }

  // "Notify winners" — stamps the draw-level winners_notified_at,
  // which drives the customer "you won" modal. One-way and freezes the winner set;
  // does NOT send the winner email (marketing owns that).
  notifyWinners(): MutationResult<DrawIdInput> {
    return useMutation({
      ...BackofficeRestMutations.loyalty.notifyWinners(),
      serializerFn: (data: DrawIdInput): DrawIdPayload => ({
        drawId: Number(data.drawId),
      }),
    })
  }

  // "Grant prizes" — the irreversible award; hands winners off to
  // reward distribution. Re-awarding an already-awarded draw returns an error.
  grantPrizes(): MutationResult<DrawIdInput> {
    return useMutation({
      ...BackofficeRestMutations.loyalty.grantPrizes(),
      serializerFn: (data: DrawIdInput): DrawIdPayload => ({
        drawId: Number(data.drawId),
      }),
    })
  }
}

export const LoyaltyMutator = new LoyaltyMutatorClass()
